import './PostView.css';
import { forwardRef, useRef, useEffect, useLayoutEffect, useImperativeHandle, useContext, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AppState } from '../context/general';
import { getSinglePost } from '../connector/post-engine';
import { newHtml } from 't-lib-ui/helpers';
import { MAX_ANON_SAVED_POSTS } from '../connector/feed-engine';
import type { UserPreferences } from '../schema-copy/api-schema';
import { createLockSvg, createCheckmarkSvg, createArrowRightSvg, createSpinnerSvg, createShowAllSvg, createTrashSvg } from './svg-helpers';
import { loadExternalScripts, isImageOnlyPost, ensureSiteCss } from 't-lib-postviewer';
import { isEmbedScriptAllowed } from './embed-consent';
import type { PostEntry } from './post-dom-factory';
import { createLoadingPlaceholder, createListSkeleton, createPostElement, updatePostStatus, createFollowActionSlot, createFollowSepSlot } from './post-dom-factory';

// ── Constants ──────────────────────────────────────────────────────────

const ESTIMATED_HEIGHT = 100;
const ESTIMATED_HEIGHT_LIST = 32;
const SCROLL_DURATION = 350;
const MARK_READ_DELAY = 500;
const BUFFER_POSTS = 3;

// ── Types ──────────────────────────────────────────────────────────────

export interface PostRendererProps {
  posts: [string, number][];
  startAt: number;
  onAllRead: () => void;
  onMark: (feedId: string, postIndex: number, type: 'star' | 'readLater' | 'read', value: boolean) => void;
  onClick: (feedId: string, postIndex: number) => void;
  onVisiblePostChange?: (feedId: string, postIndex: number) => void;
  showUnreadOnly?: boolean;
  onEndOfUnread?: () => void;
  onShowAll?: () => void;
  viewMode?: 'full' | 'single' | 'list';
  isSingleFeed?: boolean;
  userPreferences?: UserPreferences;
  overflowMessage?: string;
  onOverflowAction?: () => void;
  onLoadMore?: () => void;
  onShare?: (feedId: string, postIndex: number, postTitle: string, originalLink: string, x: number, y: number) => void;
  onFirstPostImageOnly?: (isImageOnly: boolean) => void;
  nextUnreadFeed?: { id: string; name: string; isFolder?: boolean };
  onNavigateToFeed?: (feedId: string) => void;
  onKeepAllUnread?: () => void;
  // Fires whenever lock/read state changes, so the header can mirror it: `allLocked`
  // drives the Keep-All button's filled-lock look, `allReadOrLocked` swaps the header's
  // "Mark all read" button for "Next Feed" once nothing is left to mark read.
  onLockedChange?: (state: { allLocked: boolean; allReadOrLocked: boolean }) => void;
  // Fires when the "Keep All" undo snapshot becomes available (after a Keep All) or is
  // cleared (individual read/lock change, or undo consumed), so the header can offer an
  // "Undo" entry in its context menu mirroring the bottom button's in-place undo.
  onKeepUndoChange?: (available: boolean) => void;
  readLaterCount?: number;
  isUnreadList?: boolean;
  isSavedList?: boolean;
  // The saved list shown (not set for the feed preview): posts that fail to load get a trashcan
  // that removes them from it — otherwise a post whose feed or file is gone could never leave.
  savedListType?: 'starred' | 'read-later';
  // Anonymous session viewing a saved list: show the fineprint about the saved-posts cap
  // and impermanence below the post count, nudging toward signup.
  anonSavedListNote?: boolean;
  feedCategory?: string;
  // Grey fineprint rendered at the very bottom of the list, below all action buttons.
  // Used for the private-feed / newsletter-inbox storage-limit notice.
  bottomNote?: string;
  loadMoreLabel?: string;
  // When set, the load-more ("Show more") button is paired with a "Next Feed" button,
  // as used by browse-only feeds that hide older read posts behind "Show more".
  loadMoreWithNextFeed?: boolean;
  // Rendered at the top of the scrolling list, above the first post (e.g. the FeedTile of an
  // unfollowed feed's preview). Shown even when there are no posts.
  topContent?: ReactNode;
  // Saved-list posts normally offer '+ Follow' for unfollowed feeds; the feed preview has its own.
  hideFollowButtons?: boolean;
  // No "Keep All" at the end of the list (a paused feed: its posts are read anyway).
  hideKeepAll?: boolean;
}

export interface PostRendererHandle {
  scrollToPost: (feedId: string, postIndex: number) => void;
  scrollPage: (direction: -1 | 1) => void;
  /** Smooth-scrolls the container all the way to the bottom (end of the last
   *  post / the bottom action buttons). Used by the "down" nav at the last post. */
  scrollToBottom: () => void;
  moveToTop: (immediately: boolean) => void;
  updatePostStatus: (feedId: string, postIndex: number, type: 'star' | 'readLater' | 'read', value: boolean) => void;
  markPostReadUnlessLocked: (feedId: string, postIndex: number) => void;
  markAllRead: () => void;
  markAllReadUnlessLocked: () => void;
  markAllUnread: () => void;
  keepAllUnread: () => void;
  markOlderThanRead: (days: number) => void;
  listNavigate: (direction: 1 | -1) => boolean;
  /** If the viewport is scrolled more than `thresholdPx` past the top of the
   *  current post, smooth-scrolls back to that post's top and returns true.
   *  Otherwise returns false so the caller can go to the previous post. */
  prevPostOrScrollToTop: (thresholdPx: number) => boolean;
  /** With topContent: while it is the "current" item (the first post sits more than `thresholdPx`
   *  below its snapped position), scrolls to the first post and returns true. Otherwise false,
   *  so the caller moves on to the next post as usual. */
  nextFromTopContent: (thresholdPx: number) => boolean;
  /** The post currently shown as "current" (the blue-border post the user
   *  sees) as [feedId, postIndex]. This is what j/k step relative to.
   *  Returns null when nothing is rendered yet. */
  getCurrentVisible: () => [string, number] | null;
  /** True if any post in the current feed has been manually locked (keepUnread). */
  hasLockedPosts: () => boolean;
  /** Post indexes of entries currently locked (keepUnread) for the given feed. */
  getLockedPostNumbers: (feedId: string) => number[];
  /** Toggle the "keep unread" lock on a single post (same as clicking its keep icon). */
  toggleKeepUnread: (feedId: string, postIndex: number) => void;
  /** Runs the same action as the bottom Keep-All button: Keep All → undo → mark-all-read. */
  keepAllAction: () => void;
  /** Restore the snapshot captured by the last "Keep All" (same as the button's undo). */
  undoKeepAll: () => void;
  /** Drop the "Keep All" undo snapshot without restoring (e.g. superseded by Mark All Read). */
  clearKeepUndo: () => void;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function getStableHeight(el: HTMLElement, fallback: number): number {
  const h = Math.max(
    el.getBoundingClientRect().height,
    el.offsetHeight,
    el.scrollHeight,
  );
  return h > 1 ? h : Math.max(fallback, ESTIMATED_HEIGHT);
}

// ── Component ──────────────────────────────────────────────────────────

const PostRenderer = forwardRef<PostRendererHandle, PostRendererProps>((props, ref) => {
  const { posts } = props;
  const appContext = useContext(AppState);
  const containerRef = useRef<HTMLDivElement>(null);
  // Persistent host for topContent: the lifecycle effect empties the container on every rebuild
  // and re-attaches this node first, while React keeps rendering into it through a portal.
  const topHost = useMemo(() => newHtml('div', { class: 'postsTopContent' }) as HTMLDivElement, []);
  const hasTopContent = props.topContent != undefined;
  // Read through the ref by the lifecycle effect's closures: topContent can come and go (the
  // FeedTile shown by clicking the feed name) without a rebuild.
  const hasTopContentRef = useRef(hasTopContent);
  hasTopContentRef.current = hasTopContent;
  const hideFollowRef = useRef(false);
  hideFollowRef.current = !!props.hideFollowButtons;
  const savedListTypeRef = useRef(props.savedListType);
  savedListTypeRef.current = props.savedListType;

  const startAtRef = useRef(props.startAt);
  startAtRef.current = props.startAt;
  // The posts array used by the previous build, so an append-only growth (e.g. the
  // browse-only "Show more" revealing older posts below) can preserve scroll position
  // instead of jumping back to startAt.
  const prevPostsRef = useRef<[string, number][]>([]);

  const onAllReadRef = useRef(props.onAllRead);
  onAllReadRef.current = props.onAllRead;
  const onMarkRef = useRef(props.onMark);
  onMarkRef.current = props.onMark;
  const onClickRef = useRef(props.onClick);
  onClickRef.current = props.onClick;
  const onVisiblePostChangeRef = useRef(props.onVisiblePostChange);
  onVisiblePostChangeRef.current = props.onVisiblePostChange;
  const showUnreadOnlyRef = useRef(props.showUnreadOnly);
  showUnreadOnlyRef.current = props.showUnreadOnly;
  const onEndOfUnreadRef = useRef(props.onEndOfUnread);
  onEndOfUnreadRef.current = props.onEndOfUnread;
  const onShowAllRef = useRef(props.onShowAll);
  onShowAllRef.current = props.onShowAll;
  const onOverflowActionRef = useRef(props.onOverflowAction);
  onOverflowActionRef.current = props.onOverflowAction;
  const onLoadMoreRef = useRef(props.onLoadMore);
  onLoadMoreRef.current = props.onLoadMore;
  const onShareRef = useRef(props.onShare);
  onShareRef.current = props.onShare;
  const onFirstPostImageOnlyRef = useRef(props.onFirstPostImageOnly);
  onFirstPostImageOnlyRef.current = props.onFirstPostImageOnly;
  const onKeepAllUnreadRef = useRef(props.onKeepAllUnread);
  onKeepAllUnreadRef.current = props.onKeepAllUnread;
  const onKeepUndoChangeRef = useRef(props.onKeepUndoChange);
  onKeepUndoChangeRef.current = props.onKeepUndoChange;
  const onNavigateToFeedRef = useRef(props.onNavigateToFeed);
  onNavigateToFeedRef.current = props.onNavigateToFeed;
  const viewModeRef = useRef(props.viewMode ?? 'full');
  viewModeRef.current = props.viewMode ?? 'full';
  const isSingleFeedRef = useRef(props.isSingleFeed);
  isSingleFeedRef.current = props.isSingleFeed;
  const userPreferencesRef = useRef(props.userPreferences);
  userPreferencesRef.current = props.userPreferences;
  const feedEngineRef = useRef(appContext.feedEngine);
  feedEngineRef.current = appContext.feedEngine;
  const appStateRef = useRef(appContext.state);
  appStateRef.current = appContext.state;

  const entriesRef = useRef<PostEntry[]>([]);
  const renderedRangeRef = useRef<[number, number]>([-1, -1]);
  const markAllReadVisibleRef = useRef(false);
  const markAllReadEverVisibleRef = useRef(false);
  const expandedIndexRef = useRef<number | null>(null);

  const scrollToPostFn = useRef<PostRendererHandle['scrollToPost']>(() => {});
  const scrollPageFn = useRef<PostRendererHandle['scrollPage']>(() => {});
  const scrollToBottomFn = useRef<PostRendererHandle['scrollToBottom']>(() => {});
  const moveToTopFn = useRef<PostRendererHandle['moveToTop']>(() => {});
  const updatePostStatusFn = useRef<PostRendererHandle['updatePostStatus']>(() => {});
  const markPostReadUnlessLockedFn = useRef<PostRendererHandle['markPostReadUnlessLocked']>(() => {});
  const markAllReadFn = useRef<PostRendererHandle['markAllRead']>(() => {});
  const markAllReadUnlessLockedFn = useRef<PostRendererHandle['markAllReadUnlessLocked']>(() => {});
  const markAllUnreadFn = useRef<PostRendererHandle['markAllUnread']>(() => {});
  const keepAllUnreadFn = useRef<PostRendererHandle['keepAllUnread']>(() => {});
  const markOlderThanReadFn = useRef<PostRendererHandle['markOlderThanRead']>(() => {});
  const listNavigateFn = useRef<PostRendererHandle['listNavigate']>(() => false);
  const prevPostOrScrollToTopFn = useRef<PostRendererHandle['prevPostOrScrollToTop']>(() => false);
  const nextFromTopContentFn = useRef<PostRendererHandle['nextFromTopContent']>(() => false);
  const getCurrentVisibleFn = useRef<PostRendererHandle['getCurrentVisible']>(() => null);
  const toggleKeepUnreadFn = useRef<PostRendererHandle['toggleKeepUnread']>(() => {});
  const keepAllActionFn = useRef<PostRendererHandle['keepAllAction']>(() => {});
  const undoKeepAllFn = useRef<PostRendererHandle['undoKeepAll']>(() => {});
  const clearKeepUndoFn = useRef<PostRendererHandle['clearKeepUndo']>(() => {});
  const onLockedChangeRef = useRef(props.onLockedChange);
  onLockedChangeRef.current = props.onLockedChange;

  useImperativeHandle(ref, () => ({
    scrollToPost: (...a) => scrollToPostFn.current(...a),
    scrollPage: (...a) => scrollPageFn.current(...a),
    scrollToBottom: () => scrollToBottomFn.current(),
    moveToTop: (...a) => moveToTopFn.current(...a),
    updatePostStatus: (...a) => updatePostStatusFn.current(...a),
    markPostReadUnlessLocked: (...a) => markPostReadUnlessLockedFn.current(...a),
    markAllRead: () => markAllReadFn.current(),
    markAllReadUnlessLocked: () => markAllReadUnlessLockedFn.current(),
    markAllUnread: () => markAllUnreadFn.current(),
    keepAllUnread: () => keepAllUnreadFn.current(),
    markOlderThanRead: (d) => markOlderThanReadFn.current(d),
    listNavigate: (d) => listNavigateFn.current(d),
    prevPostOrScrollToTop: (t) => prevPostOrScrollToTopFn.current(t),
    nextFromTopContent: (t) => nextFromTopContentFn.current(t),
    getCurrentVisible: () => getCurrentVisibleFn.current(),
    hasLockedPosts: () => entriesRef.current.some(e => e.keepUnread),
    getLockedPostNumbers: (feedId) => entriesRef.current
      .filter(e => e.keepUnread && e.feedId == feedId)
      .map(e => e.postIndex),
    toggleKeepUnread: (...a) => toggleKeepUnreadFn.current(...a),
    keepAllAction: () => keepAllActionFn.current(),
    undoKeepAll: () => undoKeepAllFn.current(),
    clearKeepUndo: () => clearKeepUndoFn.current(),
  }), []);

  // Attach / detach the top content in place when it appears or goes away after the build (the
  // build itself puts it first). Layout effect, so it is in place before a parent's effect scrolls up.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container?.firstChild) return;
    if (hasTopContent && topHost.parentNode != container) container.prepend(topHost);
    else if (!hasTopContent && topHost.parentNode == container) topHost.remove();
  }, [hasTopContent, topHost]);

  // Main lifecycle effect - rebuilds when posts changes
  useEffect(() => {
    const container = containerRef.current!;
    if (!container) return;
    expandedIndexRef.current = null;
    // View mode when this effect was set up. If the cleanup runs because the view mode
    // changed (vs. posts changing or unmount), we must NOT mark posts read — switching
    // full/single/list is not navigating away from them.
    const effectViewMode = viewModeRef.current;
    const estimatedH = viewModeRef.current == 'list' ? ESTIMATED_HEIGHT_LIST : ESTIMATED_HEIGHT;

    const debugScroll = false;
    const logScroll = (...args: any[]) => {
      if (debugScroll) console.log('[PostRenderer]', ...args);
    };

    const startAt = startAtRef.current;
    const isSavedList = props.isSavedList ?? false;

    // Detect an append-only growth (same posts as before, plus more at the end) so we can
    // keep the current scroll position rather than snapping back to startAt. Capture the
    // scrollTop before clearing, since emptying the container resets it to 0.
    const prevPosts = prevPostsRef.current;
    const isAppend = prevPosts.length > 0 && posts.length > prevPosts.length
      && prevPosts.every((p, i) => p[0] == posts[i][0] && p[1] == posts[i][1]);
    const preservedScrollTop = container.scrollTop;
    prevPostsRef.current = posts;

    // Clear previous content
    container.innerHTML = '';
    if (hasTopContentRef.current) container.appendChild(topHost);

    // Build entries with initial status from feedEngine
    const fe = feedEngineRef.current;
    const readCache = new Map<string, Set<number>>();
    const starCache = new Map<string, Set<number>>();
    const rlCache = new Map<string, Set<number>>();
    const getSet = (cache: Map<string, Set<number>>, feedId: string, getter: (id: string) => Set<number>) => {
      let s = cache.get(feedId);
      if (!s) { s = getter(feedId); cache.set(feedId, s); }
      return s;
    };

    const entries: PostEntry[] = posts.map(([feedId, postIndex]) => {
      const isRead = getSet(readCache, feedId, id => fe.getReadForFeed(id)).has(postIndex);
      return {
        feedId, postIndex,
        element: null,
        height: estimatedH,
        loaded: false,
        data: null,
        isRead,
        initiallyRead: isRead,
        isStarred: getSet(starCache, feedId, id => fe.getStarredForFeed(id)).has(postIndex),
        isReadLater: getSet(rlCache, feedId, id => fe.getReadLaterForFeed(id)).has(postIndex),
        manuallyUnread: false,
        keepUnread: false,
      };
    });
    entriesRef.current = entries;
    // Fresh entries always start unlocked (keepUnread defaults to false), so reset any
    // header lock state left over from the previously viewed feed.
    onLockedChangeRef.current?.({
      allLocked: false,
      allReadOrLocked: entries.length > 0 && entries.every(e => e.isRead || e.keepUnread),
    });

    if (!entries.length) {
      renderedRangeRef.current = [-1, -1];
      return;
    }

    // Create upper spacer
    const spacer = document.createElement('div');
    spacer.style.height = `${estimatedH * Math.max(0, startAt - 1)}px`;
    spacer.style.flexShrink = '0';
    container.appendChild(spacer);

    // Create bottom spacer (absorbs height of trimmed bottom elements)
    const bottomSpacer = document.createElement('div');
    bottomSpacer.style.flexShrink = '0';
    let bottomSpacerHeight = 0;
    container.appendChild(bottomSpacer);

    // Create mark-all-read button. With auto-read on it stays hidden — the
    // end-of-list action row (Keep Unread / Next Feed) takes its place.
    // For saved lists (starred / read-later) show a simple post count instead of a button.
    const isRegularFeed = !props.isSavedList && !props.isUnreadList;
    let markAllReadBtn: HTMLButtonElement | null = null;
    let bottomContent: HTMLElement;
    if (props.isSavedList) {
      const bottomChildren: HTMLElement[] = [newHtml('span', { class: 'postCountBottom' },
        entries.length == 1 ? '1 post' : `${entries.length} posts`)];
      if (props.anonSavedListNote)
        bottomChildren.push(newHtml('p', { class: 'savedListNote' }, [
          `Without an account, only your latest ${MAX_ANON_SAVED_POSTS} saved posts are kept, in this browser only — older posts may disappear. `,
          newHtml('a', { href: '/signup' }, 'Sign up'),
          ' for permanent storage of your posts.',
        ]));
      bottomContent = newHtml('div', { class: 'savedListBottom' }, bottomChildren);
    } else {
      markAllReadBtn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable' }, 'Mark all as read') as HTMLButtonElement;
      markAllReadBtn.addEventListener('click', () => onAllReadRef.current());
      bottomContent = markAllReadBtn;
    }
    const hideMarkAllRead = showUnreadOnlyRef.current || props.onLoadMore || !props.isSavedList;
    const markAllReadEl = newHtml('div', { class: 'markAllReadBottom',
      style: { display: hideMarkAllRead ? 'none' : '' } }, bottomContent);
    container.appendChild(markAllReadEl);

    // ── Shared "Keep All" / "Items Locked" button machinery ──────────
    // The button stays in place and only swaps its icon + label. After a "Keep All"
    // we keep an undo snapshot so a second click reverses it; locking any post by hand
    // invalidates that undo (see toggleKeepUnreadFn), and a hand-locked "all locked"
    // state instead marks everything read on the next click.
    type KeepSnapshot = { feedId: string; postIndex: number; isRead: boolean; keepUnread: boolean; manuallyUnread: boolean };
    let keepAllUndo: KeepSnapshot[] | null = null;
    // Single writer for keepAllUndo so the header's context-menu "Undo" entry stays in
    // sync with the snapshot (notifies on capture, clear and consume).
    const setKeepUndo = (snapshot: KeepSnapshot[] | null) => {
      const had = !!keepAllUndo;
      keepAllUndo = snapshot;
      if (had != !!snapshot) onKeepUndoChangeRef.current?.(!!snapshot);
    };
    clearKeepUndoFn.current = () => setKeepUndo(null);
    // A rebuild (feed/sort/view change, poll, append, "show more") discards the snapshot
    // held by the previous closure, so tell the header any keep-all Undo is gone.
    onKeepUndoChangeRef.current?.(false);

    // "Locked" = every post is either kept-unread or was already read on entry, and at
    // least one is kept. Reached via "Keep All" or by locking each post individually.
    const isAllLocked = () => entries.every(e => e.keepUnread || e.initiallyRead) && entries.some(e => e.keepUnread);
    // True when nothing is left to mark read (every post is read or locked). Drives the
    // Next-Feed/Home icon: a checkmark while there are unread posts to mark, else an arrow.
    const isAllReadOrLocked = () => entries.length > 0 && entries.every(e => e.isRead || e.keepUnread);

    const restoreKeepAllUndo = () => {
      if (!keepAllUndo) return;
      for (const s of keepAllUndo) {
        const entry = entries.find(e => e.feedId == s.feedId && e.postIndex == s.postIndex);
        if (!entry) continue;
        entry.isRead = s.isRead;
        entry.keepUnread = s.keepUnread;
        entry.manuallyUnread = s.manuallyUnread;
        onMarkRef.current(entry.feedId, entry.postIndex, 'read', entry.isRead);
        if (entry.element) updatePostStatus(entry.element, entry, true);
      }
      setKeepUndo(null);
      updateMarkAllRead();
    };
    undoKeepAllFn.current = restoreKeepAllUndo;

    // Locked with no undo (locked post-by-post): a click clears every lock and marks read.
    const markAllReadAndUnlock = () => {
      let changed = false;
      for (const entry of entries) {
        if (entry.isRead && !entry.keepUnread && !entry.manuallyUnread) continue;
        entry.isRead = true;
        entry.keepUnread = false;
        entry.manuallyUnread = false;
        onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    const onKeepButtonClick = () => {
      if (!isAllLocked()) {
        keepAllUnreadFn.current();        // captures keepAllUndo
        onKeepAllUnreadRef.current?.();
      } else if (keepAllUndo) {
        restoreKeepAllUndo();
      } else {
        markAllReadAndUnlock();
      }
    };
    // Let the header's Keep-All button trigger the same behaviour.
    keepAllActionFn.current = onKeepButtonClick;

    // Builds a Keep-All button that swaps to a filled-lock "Items Locked" state in
    // place. The two states (each its own icon + label) share one grid cell: the cell
    // reserves the wider state's width so the button never resizes, while each state is
    // independently centred — keeping a constant icon↔text gap and a centred group.
    const buildKeepAllButton = () => {
      const lockedText = 'Keeping all';
      const btn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable keepAllUnreadBtn' }, [
        newHtml('span', { class: 'keepLabelStack' }, [
          newHtml('span', { class: 'keepState keepStateDefault' }, [createLockSvg(14), newHtml('span', {}, 'Keep All')]),
          newHtml('span', { class: 'keepState keepStateLocked' }, [createLockSvg(14), newHtml('span', {}, lockedText)]),
        ]),
      ]) as HTMLButtonElement;
      btn.addEventListener('click', onKeepButtonClick);
      const setLocked = (locked: boolean) => btn.classList.toggle('locked', locked);
      return { btn, setLocked };
    };

    // Next-Feed / Home button. Both also mark the remaining unread posts read, so the
    // leading icon is a checkmark while unread posts remain and a right arrow once
    // everything is read or locked (pure navigation). setAllRead swaps it on state change.
    // When unreadLabel is given the label swaps with the icon: the plain label while
    // everything is read (arrow), the "mark all read" label while unread posts remain
    // (checkmark). unreadShortLabel is a narrow-screen alternate toggled by CSS.
    const makeNavButton = (label: string, unreadLabel?: string, unreadShortLabel?: string) => {
      const check = createCheckmarkSvg(14);
      const arrow = createArrowRightSvg(14);
      const labelEl = newHtml('span', { style: { marginLeft: '6px' } }, unreadLabel
        ? [
            newHtml('span', { class: 'navLabelRead' }, label),
            newHtml('span', { class: 'navLabelUnreadFull' }, unreadLabel),
            newHtml('span', { class: 'navLabelUnreadShort' }, unreadShortLabel ?? unreadLabel),
          ]
        : label);
      const btn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable sidewayFocus' },
        [check, arrow, labelEl]) as HTMLButtonElement;
      const setAllRead = (allRead: boolean) => {
        check.style.display = allRead ? 'none' : 'inline-block';
        arrow.style.display = allRead ? 'inline-block' : 'none';
        btn.classList.toggle('navAllRead', allRead);
      };
      setAllRead(isAllReadOrLocked());
      return { btn, setAllRead };
    };

    // Keep-unread button for unread-list mode (shown at bottom when auto-read is on)
    let checkAllKeptBottom: (() => void) | undefined;
    if (props.isUnreadList) {
      const { btn: keepBtn, setLocked: setKeepLocked } = buildKeepAllButton();
      // Navigation button next to Keep All — marks all non-locked posts as read
      // first. In the folder-limited unread list the next stop (next folder with
      // unread posts, or its single unread feed) comes in via nextUnreadFeed;
      // the plain all-items list has nothing after it, so the button leads Home.
      // Hidden when everything is locked (nothing to mark, nothing to skip past).
      const unreadListTarget = props.nextUnreadFeed;
      const { btn: morePostsBtn, setAllRead: setMorePostsAllRead } = unreadListTarget
        ? makeNavButton(unreadListTarget.isFolder ? 'Next Folder' : 'Next Feed', 'Mark all read, next', 'Mark & next')
        : makeNavButton('Home', 'Mark all read, home', 'Mark & home');
      const morePostsCol = newHtml('div', { class: 'endOfUnreadNextFeed' }, [morePostsBtn]);
      if (unreadListTarget)
        morePostsCol.appendChild(newHtml('span', { class: 'endOfUnreadNextFeedName' }, unreadListTarget.name));
      morePostsBtn.addEventListener('click', () => {
        for (const entry of entriesRef.current) {
          if (entry.isRead || entry.manuallyUnread) continue;
          entry.isRead = true;
          onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
          if (entry.element) updatePostStatus(entry.element, entry, true);
        }
        onNavigateToFeedRef.current?.(unreadListTarget?.id ?? 'home');
      });

      checkAllKeptBottom = () => {
        const locked = isAllLocked();
        setKeepLocked(locked);
        morePostsCol.style.display = locked ? 'none' : '';
        setMorePostsAllRead(isAllReadOrLocked());
      };

      const bottomActionRow = newHtml('div', { class: 'endOfUnreadActions' }, [keepBtn, morePostsCol]);
      const keepUnreadEl = newHtml('div', { class: 'markAllReadBottom',
        style: { display: props.onLoadMore ? 'none' : '' } }, bottomActionRow);
      container.appendChild(keepUnreadEl);
    }

    // End-of-unread element (shown when in unread-only mode, hidden in load-more mode)
    const endOfUnreadChildren: HTMLElement[] = [];
    let checkAllKept: (() => void) | undefined;

    {
      // Action row: keep-unread (left) + next feed (right)
      const actionRow = newHtml('div', { class: 'endOfUnreadActions' });

      // Next feed with unread posts (cyclic; undefined when none are left).
      const targetFeed = props.nextUnreadFeed;

      const { btn: keepBtn, setLocked: setKeepLocked } = buildKeepAllButton();
      let nextBtn: HTMLElement | undefined;
      let setNextAllRead: ((allRead: boolean) => void) | undefined;
      // When everything is locked there's nothing to skip past, so the Next Feed /
      // Home button drops out of sideways keyboard navigation.
      checkAllKept = () => {
        const locked = isAllLocked();
        setKeepLocked(locked);
        nextBtn?.classList.toggle('sidewayFocus', !locked);
        setNextAllRead?.(isAllReadOrLocked());
      };

      // First slot reveals the posts the current view hides, when there are any:
      // "Show more" for a truncated show-all list (older read posts), "Show all posts"
      // in unread-only mode (read posts). Not for the all-items aggregate list.
      if (!props.isUnreadList) {
        if (props.onLoadMore) {
          const showMoreBtn = newHtml('button', { class: 'markAllReadBottomButton keepAllUnreadBtn flowFocussable' },
            props.loadMoreLabel ?? 'Show more');
          showMoreBtn.addEventListener('click', () => onLoadMoreRef.current?.());
          actionRow.appendChild(showMoreBtn);
        } else if (showUnreadOnlyRef.current && onShowAllRef.current) {
          // Two labels: the short one ("Show all") shows on narrow screens (see PostView.css).
          const showAllBtn = newHtml('button', { class: 'markAllReadBottomButton keepAllUnreadBtn flowFocussable' }, [
            createShowAllSvg(14),
            newHtml('span', { class: 'showAllFull' }, 'Show all posts'),
            newHtml('span', { class: 'showAllShort' }, 'Show all'),
          ]);
          showAllBtn.addEventListener('click', () => onShowAllRef.current?.());
          actionRow.appendChild(showAllBtn);
        }
      }

      if (!props.hideKeepAll) actionRow.appendChild(keepBtn);

      const markAllNonLockedRead = () => {
        for (const entry of entries) {
          if (entry.isRead || entry.manuallyUnread) continue;
          entry.isRead = true;
          onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
          if (entry.element) updatePostStatus(entry.element, entry, true);
        }
      };

      // In a regular feed, "Next Feed" goes to the next/prev unread feed (with its
      // name shown below). In the unread-posts list (all-items), there's no
      // specific next feed \u2014 the button reads "Home" and leads to the
      // Home page. Either way, mark non-locked posts as read first.
      const nextCol = newHtml('div', { class: 'endOfUnreadNextFeed' });
      if (targetFeed) {
        const nav = makeNavButton(targetFeed.isFolder ? 'Next Folder' : 'Next Feed', 'Mark all read, next', 'Mark & next');
        nextBtn = nav.btn; setNextAllRead = nav.setAllRead;
        nextBtn.addEventListener('click', () => { markAllNonLockedRead(); onNavigateToFeedRef.current?.(targetFeed.id); });
        nextCol.appendChild(nextBtn);
        nextCol.appendChild(newHtml('span', { class: 'endOfUnreadNextFeedName' }, targetFeed.name));
      } else {
        const nav = makeNavButton('Home', 'Mark all read, home', 'Mark & home');
        nextBtn = nav.btn; setNextAllRead = nav.setAllRead;
        nextBtn.addEventListener('click', () => { markAllNonLockedRead(); onNavigateToFeedRef.current?.('home'); });
        nextCol.appendChild(nextBtn);
      }
      actionRow.appendChild(nextCol);

      endOfUnreadChildren.push(actionRow);
    }

    // With auto-read on, the action row is the standard end-of-list UI for any regular
    // feed (unread-only or show-all).
    const showEndOfList = isRegularFeed;
    const endOfUnreadEl = newHtml('div', { class: 'endOfUnread',
      style: { display: showEndOfList ? '' : 'none' } }, endOfUnreadChildren);
    container.appendChild(endOfUnreadEl);

    // "Load More" button (replaces mark-all-read + overflow when onLoadMore is set).
    // Skipped when the end-of-list action row already carries the "Show more" button.
    if (props.onLoadMore && !showEndOfList) {
      const loadMoreBtn = newHtml('button',
        { class: `markAllReadBottomButton flowFocussable${props.loadMoreWithNextFeed ? ' keepAllUnreadBtn' : ''}` },
        props.loadMoreLabel ?? 'Load more');
      loadMoreBtn.addEventListener('click', () => onLoadMoreRef.current?.());
      container.appendChild(newHtml('div', { class: 'markAllReadBottom' }, loadMoreBtn));
    }

    // Overflow message (shown when more posts exist beyond the displayed limit)
    if (props.overflowMessage && !props.onLoadMore) {
      const overflowBtn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable' }, 'Mark all as read and load more');
      overflowBtn.addEventListener('click', () => onOverflowActionRef.current?.());
      container.appendChild(newHtml('div', { class: 'markAllReadBottom' }, [
        newHtml('p', { class: 'endOfUnreadText' }, props.overflowMessage),
        overflowBtn,
      ]));
    }

    if (props.bottomNote)
      container.appendChild(newHtml('p', { class: 'feedStorageNote' }, props.bottomNote));

    // IntersectionObserver for data-unrendered removal
    const viewportObserver = new IntersectionObserver(observed => {
      for (const oe of observed)
        if (oe.isIntersecting) delete (oe.target as HTMLElement).dataset.unrendered;
    }, { root: container });

    // Scroll mode declared early so ResizeObserver callback can reference it
    let scrollMode: 'idle' | 'scrolling' | 'watching' = 'idle';
    let lastScrollTop = 0;
    let lastScrollDirection: -1 | 0 | 1 = 0;

    // Above-viewport height compensation must NOT run while the user is actively
    // dragging the content upward (it would push the viewport down and fight the
    // gesture). We can't key this off lastScrollDirection: that reflects ANY
    // scroll, including the programmatic up-scroll a nav performs, so after
    // navigating up it stays -1 and would wrongly disable compensation forever —
    // exactly when late-decoding images above the viewport shift the target.
    // Track genuine user gestures (wheel/touch) with a short freshness window
    // instead, so compensation resumes the moment the gesture stops.
    const ACTIVE_USER_SCROLL_MS = 150;
    let userScrollDir: -1 | 0 | 1 = 0;
    let userScrollAt = -Infinity;
    let lastTouchY = 0;
    const userScrollingUp = () =>
      userScrollDir < 0 && performance.now() - userScrollAt < ACTIVE_USER_SCROLL_MS;

    // ResizeObserver to track post height changes (e.g. after image loads)
    const resizeMap = new Map<Element, number>();
    const resizeObserver = new ResizeObserver(observed => {
      for (const oe of observed) {
        const idx = resizeMap.get(oe.target);
        if (idx == null) continue;
        const el = oe.target as HTMLElement;
        const rect = el.getBoundingClientRect();
        const oldH = entries[idx].height;
        const nextH = getStableHeight(el, oldH);
        if (nextH == oldH) continue;
        entries[idx].height = nextH;

        const isAboveViewport = rect.bottom < container.getBoundingClientRect().top;
        // Compensation is only ours to do while the scroll machine is idle:
        // during 'scrolling'/'watching' it re-anchors the target post itself
        // every frame, so a second adjustment here double-shifts the view and
        // the resulting scrollTop mismatch makes the watcher believe the user
        // scrolled, killing it with the view left off by that delta. While the
        // user is actively dragging upward, skip too: forcing the viewport down
        // would fight the gesture.
        if (isAboveViewport && scrollMode == 'idle' && !userScrollingUp()) {
          const delta = nextH - oldH;
          container.scrollTop += delta;
          logScroll('resize compensation applied', {
            idx,
            delta,
            oldH,
            newH: nextH,
            lastScrollDirection,
            scrollMode,
          });
        } else if (debugScroll && isAboveViewport) {
          logScroll('resize compensation skipped', {
            idx,
            oldH,
            newH: nextH,
            lastScrollDirection,
            scrollMode,
          });
        }
      }
    });

    // Mark-all-read visibility observer
    // Observe BOTH the mark-all-read button AND the end-of-unread element
    // (only one is visible at a time depending on unread-only mode)
    markAllReadVisibleRef.current = false;
    markAllReadEverVisibleRef.current = false;
    let userHasScrolled = false; // Guard: only set "ever visible" after user actually interacts.
    // Must be driven by input events, not scroll events — programmatic scrollTop assignments
    // during feed loading fire scroll events that would otherwise falsely trip this flag.
    const markUserInteracted = () => { userHasScrolled = true; };
    // Record direction of genuine user scroll gestures (see userScrollingUp).
    const onUserWheel = (ev: WheelEvent) => {
      userHasScrolled = true;
      if (ev.deltaY) { userScrollDir = ev.deltaY < 0 ? -1 : 1; userScrollAt = performance.now(); }
    };
    const onUserTouchStart = (ev: TouchEvent) => { userHasScrolled = true; lastTouchY = ev.touches[0].clientY; };
    const onUserTouchMove = (ev: TouchEvent) => {
      // Finger moving down (y increases) reveals content above = scrolling up.
      const y = ev.touches[0].clientY;
      if (y != lastTouchY) { userScrollDir = y > lastTouchY ? -1 : 1; userScrollAt = performance.now(); }
      lastTouchY = y;
    };
    container.addEventListener('wheel', onUserWheel, { passive: true });
    container.addEventListener('touchstart', onUserTouchStart, { passive: true });
    container.addEventListener('touchmove', onUserTouchMove, { passive: true });
    container.addEventListener('pointerdown', markUserInteracted, { passive: true });
    container.addEventListener('keydown', markUserInteracted);
    const markAllObserver = new IntersectionObserver((entries) => {
      const wasVisible = markAllReadVisibleRef.current; // Capture BEFORE loop to avoid side effects
      for (const e of entries) {
        const isVisible = e.isIntersecting;
        if (isVisible) {
          markAllReadVisibleRef.current = true;
          if (!wasVisible && userHasScrolled) {
            markAllReadEverVisibleRef.current = true;
          }
        } else if (e.target === markAllReadEl || e.target === endOfUnreadEl) {
          // Only set to false if BOTH elements are not intersecting
          const anyVisible = entries.some(entry => entry.isIntersecting);
          markAllReadVisibleRef.current = anyVisible;
        }
      }
    }, { threshold: 0 });
    markAllObserver.observe(markAllReadEl);
    markAllObserver.observe(endOfUnreadEl);

    // ── Local Helpers ────────────────────────────────────────────────

    function getFeedInfo(feedId: string) {
      const fi = feedEngineRef.current.getFeed(feedId);
      // Every post render path comes through here, so this is where a feed's site stylesheet
      // gets loaded (a no-op after the first time).
      if (fi) ensureSiteCss(fi);
      return fi;
    }

    function insertPost(i: number, before?: HTMLElement | null) {
      const entry = entries[i];
      if (entry.element) return;

      let el: HTMLDivElement;
      if (entry.loaded && entry.data) {
        const fi = getFeedInfo(entry.feedId);
        el = createPostElement(
          entry,
          fi ? (fi.shortTitle || fi.title) : '',
          fi?.siteUrl,
          viewModeRef.current,
          true,
          isSingleFeedRef.current,
          isSavedList,
          isSavedList ? (hideFollowRef.current || (appStateRef.current?.currentUser?.feeds.includes(entry.feedId) ?? false)) : undefined,
        );
      } else {
        el = viewModeRef.current == 'list'
          ? createListSkeleton(isSingleFeedRef.current, i)
          : createLoadingPlaceholder(estimatedH);
      }
      entry.element = el;
      container.insertBefore(el, before || bottomSpacer);
      viewportObserver.observe(el);
      resizeObserver.observe(el);
      resizeMap.set(el, i);
      if (i == currentVisibleIdx)
        entry.element.querySelector('.postCard')?.classList.add('currentPost');

      // Virtualization re-mounts a fresh embed blockquote; tell widget libs to re-scan.
      if (entry.loaded && entry.data?.externalScripts)
        loadExternalScripts(entry.data.externalScripts, undefined, isEmbedScriptAllowed);


      if (!entry.loaded) startFetch(i);
    }

    // In-flight post loads by entry index, so list navigation can wait for a row's
    // data instead of racing it (see toggleListExpand) without starting a second fetch.
    const fetching = new Map<number, Promise<void>>();
    function startFetch(i: number): Promise<void> {
      const inFlight = fetching.get(i);
      if (inFlight) return inFlight;
      const p = fetchPost(i).finally(() => fetching.delete(i));
      fetching.set(i, p);
      return p;
    }

    async function fetchPost(i: number) {
      const entry = entries[i];
      if (entry.loaded) return;

      // Unsubscribed feeds (possible in saved lists) aren't in the engine's
      // feed list yet — resolve their metadata via the star lists instead of
      // leaving the placeholder empty forever.
      const fi = getFeedInfo(entry.feedId) ?? await feedEngineRef.current.ensureFeedInfo(entry.feedId).catch(() => undefined);
      if (!fi) {
        showPostLoadError(entry);
        return;
      }

      try {
        const post = await getSinglePost(
          { id: fi.id, postCount: fi.postCount, passphrase: fi.passphrase, cdn: fi.cdn, syncInstance: fi.syncInstance },
          entry.postIndex
        );
        entry.data = post;
        entry.loaded = true;

        if (i == 0)
          onFirstPostImageOnlyRef.current?.(isImageOnlyPost(post.description));

        if (!entry.element?.isConnected) return;

        const entryRectBefore = entry.element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const oldH = getStableHeight(entry.element, entry.height);
        const wasAbove = entryRectBefore.bottom < containerRect.top;
        const distanceAboveViewport = containerRect.top - entryRectBefore.bottom;

        const newEl = createPostElement(
          entry,
          fi.shortTitle || fi.title,
          fi.siteUrl,
          viewModeRef.current,
          true,
          isSingleFeedRef.current,
          isSavedList,
          isSavedList ? (hideFollowRef.current || (appStateRef.current?.currentUser?.feeds.includes(entry.feedId) ?? false)) : undefined,
        );
        viewportObserver.unobserve(entry.element);
        resizeObserver.unobserve(entry.element);
        resizeMap.delete(entry.element);
        entry.element.replaceWith(newEl);
        entry.element = newEl;
        viewportObserver.observe(newEl);
        resizeObserver.observe(newEl);
        resizeMap.set(newEl, i);
        if (i == currentVisibleIdx)
          newEl.querySelector('.postCard')?.classList.add('currentPost');

        // Single-item list mode: auto-expand once data loads
        if (entries.length == 1 && viewModeRef.current == 'list')
          requestAnimationFrame(() => { if (expandedIndexRef.current == null && entry.element?.isConnected) toggleListExpand(0); });

        const newH = getStableHeight(newEl, oldH);
        entry.height = newH;

        // Compensate only while idle (the scroll machine anchors the target
        // itself while scrolling/watching — see the ResizeObserver handler)
        // and not while the user is actively dragging upward, where forcing
        // downward compensation from placeholder->real height changes would
        // fight the gesture. Any distance above counts: the placeholder directly
        // above the current post (what scrollToPost's teleport inserts for the
        // previous post) is the one whose late load shoves the view the furthest.
        const shouldCompensateForAbove =
          wasAbove &&
          scrollMode == 'idle' &&
          !userScrollingUp();

        if (shouldCompensateForAbove) {
          const delta = newH - oldH;
          container.scrollTop += delta;
          logScroll('fetchPost compensation applied', {
            idx: i,
            delta,
            oldH,
            newH,
            distanceAboveViewport,
            lastScrollDirection,
          });
        } else if (debugScroll && wasAbove) {
          logScroll('fetchPost compensation skipped', {
            idx: i,
            oldH,
            newH,
            distanceAboveViewport,
            lastScrollDirection,
            scrollMode,
          });
        }

        loadExternalScripts(post.externalScripts, undefined, isEmbedScriptAllowed);
      } catch (e) {
        console.error(`Failed to fetch post ${entry.feedId}/${entry.postIndex}:`, e);
        showPostLoadError(entry, fi);
      }
    }

    /** Replaces the placeholder with an error naming the feed (when known). In a saved list it
     *  also gets a trashcan that removes the post from the list. */
    function showPostLoadError(entry: PostEntry, fi?: { title: string, shortTitle?: string, postCount?: number }) {
      if (!entry.element?.isConnected) return;
      const feedName = fi && (fi.shortTitle || fi.title);
      const listType = savedListTypeRef.current;
      entry.element.innerHTML = '';
      entry.element.appendChild(newHtml('div', {
        class: 'postLoadError',
        'data-feed-id': entry.feedId, 'data-post-index': String(entry.postIndex),
        'data-post-count': String(fi?.postCount ?? '?'),
      }, [
        newHtml('span', {}, feedName ? `Failed to load post from ${feedName}` : 'Failed to load post (unknown feed)'),
        // The slot is positioned, not the button: the focus style sets the button's position.
        ...(listType ? [newHtml('span', { class: 'postLoadErrorAction' }, newHtml('button', {
          // Not sidewayFocus: the row has no other stop to reach it from with left/right.
          class: 'actionButton flowFocussable',
          title: listType == 'starred' ? 'Remove from Starred' : 'Remove from Read Later',
          'aria-label': listType == 'starred' ? 'Remove from Starred' : 'Remove from Read Later',
          'data-action': 'removeSaved',
        }, createTrashSvg(18)))] : []),
      ]));
    }

    /** The trashcan of a post that failed to load: take it off the saved list shown. */
    function removeFailedSavedPost(errorEl: HTMLElement) {
      const listType = savedListTypeRef.current;
      const entry = entries.find(e => e.feedId == errorEl.dataset.feedId && e.postIndex == Number(errorEl.dataset.postIndex));
      if (!listType || !entry) return;
      if (listType == 'starred') entry.isStarred = false;
      else entry.isReadLater = false;
      onMarkRef.current(entry.feedId, entry.postIndex, listType == 'starred' ? 'star' : 'readLater', false);
      // Used from the keyboard, the button vanishes under the focus: hand it to the next stop.
      if (errorEl.contains(document.activeElement)) {
        const stops = Array.from(container.querySelectorAll<HTMLElement>('.flowFocussable:not(.sidewayFocus)'));
        const next = stops.find(el => errorEl.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING && !errorEl.contains(el));
        next?.focus({ preventScroll: true });
      }
      errorEl.replaceChildren(newHtml('span', {}, listType == 'starred' ? 'Removed from Starred' : 'Removed from Read Later'));
    }

    function updateMarkAllRead() {
      const allRead = entries.every(e => e.isRead);
      if (markAllReadBtn) {
        markAllReadBtn.disabled = allRead;
        markAllReadBtn.textContent = allRead ? 'All items read' : 'Mark all as read';
      }
      checkAllKept?.();
      checkAllKeptBottom?.();
      onLockedChangeRef.current?.({
        allLocked: isAllLocked(),
        allReadOrLocked: entries.length > 0 && entries.every(e => e.isRead || e.keepUnread),
      });
    }

    // ── Virtualization ──────────────────────────────────────────────

    function handleScrollUpdate() {
      let [first, last] = renderedRangeRef.current;
      if (first < 0) return;

      const cr = container.getBoundingClientRect();
      const bufferPx = BUFFER_POSTS * estimatedH;

      // Extend below
      while (last < entries.length - 1) {
        const el = entries[last].element;
        if (el && el.getBoundingClientRect().bottom > cr.bottom + bufferPx) break;
        last++;
        bottomSpacerHeight = Math.max(0, bottomSpacerHeight - entries[last].height);
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;
        insertPost(last);
      }

      // Extend above. Loop until the spacer no longer intrudes into the
      // viewport+buffer (mirrors "Extend below"), re-measuring each iteration.
      // A fixed per-call chunk can't keep up with fast upward scrolling and
      // leaves the viewport sitting inside the blank top spacer.
      if (first > 0 && spacer.getBoundingClientRect().bottom > cr.top - bufferPx) {
        const newFirst0 = first;
        const anchorEl = entries[first].element!;
        const anchorTop = anchorEl.getBoundingClientRect().top;
        const scrollTopBeforeInsert = container.scrollTop;

        while (first > 0 && spacer.getBoundingClientRect().bottom > cr.top - bufferPx) {
          first--;
          const h = entries[first].height;
          const sh = Math.max(0, parseFloat(spacer.style.height) - h);
          spacer.style.height = `${sh}px`;
          insertPost(first, entries[first + 1]?.element);
        }

        const scrollTopAfterInsert = container.scrollTop;
        const anchorDelta = anchorEl.getBoundingClientRect().top - anchorTop;
        container.scrollTop += anchorDelta;
        logScroll('extend above', {
          newFirst0,
          newFirst: first,
          anchorDelta,
          scrollTopBeforeInsert,
          scrollTopAfterInsert,
          scrollTopAfterAnchorAdjust: container.scrollTop,
        });
      }

      // Skip trimming during scroll animation/watching — the target element must stay in the DOM
      if (scrollMode == 'idle') {
        // Trim from top
        const trimPx = (BUFFER_POSTS + 5) * estimatedH;
        while (first < last) {
          const el = entries[first].element;
          // The expanded list row is the navigation anchor: never trim it, its cached
          // height would inflate the spacer and the next step would lose its position.
          if (!el || first == expandedIndexRef.current || el.getBoundingClientRect().bottom >= cr.top - trimPx) break;

          const h = getStableHeight(el, entries[first].height);
          entries[first].height = h;
          viewportObserver.unobserve(el);
          resizeObserver.unobserve(el);
          resizeMap.delete(el);
          // Grow the spacer and flush layout (offsetHeight) BEFORE removing the element.
          // Safari lays out synchronously while tearing down a nested scroll container
          // (every .postBody figure is overflow-x: auto) and does so without the pending
          // spacer style; with the content momentarily shorter it clamps scrollTop, which
          // at the very bottom of the feed throws the view up by the removed post's height.
          spacer.style.height = `${(parseFloat(spacer.style.height) || 0) + h}px`;
          void spacer.offsetHeight;
          el.remove();
          entries[first].element = null;
          first++;
        }

        // Trim from bottom
        while (last > first) {
          const el = entries[last].element;
          if (!el || last == expandedIndexRef.current || el.getBoundingClientRect().top <= cr.bottom + trimPx) break;

          const h = getStableHeight(el, entries[last].height);
          entries[last].height = h;
          viewportObserver.unobserve(el);
          resizeObserver.unobserve(el);
          resizeMap.delete(el);
          // Spacer first and flushed, as when trimming from the top.
          bottomSpacerHeight += h;
          bottomSpacer.style.height = `${bottomSpacerHeight}px`;
          void bottomSpacer.offsetHeight;
          el.remove();
          entries[last].element = null;
          last--;
        }
      }

      renderedRangeRef.current = [first, last];
    }

    // ── Scroll State Machine ─────────────────────────────────────────

    const WATCH_DURATION = 5000;
    let scrollTarget: { kind: 'absolute'; position: number } | { kind: 'post'; idx: number } | { kind: 'bottom' } | null = null;
    let scrollStartTop = 0;
    let scrollStartTime = 0;
    let watchStartTime = 0;
    let watchExpectedScrollTop = 0;
    let watchLastTargetTop = 0;
    let scrollLoopId = 0;

    function getScrollTargetPos(): number | null {
      if (!scrollTarget) return null;
      if (scrollTarget.kind == 'absolute') return scrollTarget.position;
      // Re-evaluated every frame: content growing during the animation moves the target along.
      if (scrollTarget.kind == 'bottom') return container.scrollHeight - container.clientHeight;
      const entry = entries[scrollTarget.idx];
      if (!entry?.element) return null;
      const r = entry.element.getBoundingClientRect();
      const cr = container.getBoundingClientRect();
      return container.scrollTop + r.top - cr.top + (viewModeRef.current == 'list' ? 4 : 11);
    }

    function beginScrolling(newTarget: NonNullable<typeof scrollTarget>) {
      cancelAnimationFrame(rafId);
      scrollTarget = newTarget;
      scrollStartTop = container.scrollTop;
      scrollStartTime = 0;
      scrollMode = 'scrolling';
      container.classList.add('scrollAnimating');
      scheduleScrollLoop();
    }

    function goIdle() {
      scrollMode = 'idle';
      scrollTarget = null;
      container.classList.remove('scrollAnimating');
    }

    function scheduleScrollLoop() {
      cancelAnimationFrame(scrollLoopId);
      scrollLoopId = requestAnimationFrame(scrollLoop);
    }

    function scrollLoop(ts: number) {
      if (scrollMode == 'scrolling') {
        if (!scrollStartTime) scrollStartTime = ts;
        handleScrollUpdate();

        const targetPos = getScrollTargetPos();
        if (targetPos == null) {
          scheduleScrollLoop();
          return;
        }

        const t = Math.min((ts - scrollStartTime) / SCROLL_DURATION, 1);
        container.scrollTop = scrollStartTop + (targetPos - scrollStartTop) * easeInOutCubic(t);

        if (t >= 1) {
          if (scrollTarget?.kind == 'post') {
            const entry = entries[scrollTarget.idx];
            scrollMode = 'watching';
            watchStartTime = ts;
            watchExpectedScrollTop = container.scrollTop;
            watchLastTargetTop = entry?.element ? entry.element.getBoundingClientRect().top : 0;
            container.classList.remove('scrollAnimating');
          } else {
            goIdle();
            handleScrollUpdate();
            // Absolute scrolls (Space/page/scroll-to-bottom) finish here without
            // ever passing through onScroll's tracking (it ignores the
            // programmatic scroll events fired while 'scrolling'). Refresh the
            // visible-post anchor now, exactly as a settled manual scroll would,
            // so a following up/down nav steps from the post the user actually
            // sees instead of a stale currentVisibleIdx.
            trackVisiblePost();
            return;
          }
        }
        scheduleScrollLoop();
        return;
      }

      if (scrollMode == 'watching') {
        if (ts - watchStartTime > WATCH_DURATION) {
          goIdle();
          return;
        }

        if (Math.abs(container.scrollTop - watchExpectedScrollTop) > 1) {
          goIdle();
          return;
        }

        if (scrollTarget?.kind == 'post') {
          const entry = entries[scrollTarget.idx];
          if (entry?.element) {
            const currentTop = entry.element.getBoundingClientRect().top;
            const delta = currentTop - watchLastTargetTop;
            if (Math.abs(delta) > 0.5) {
              container.scrollTop += delta;
              watchExpectedScrollTop = container.scrollTop;
              watchLastTargetTop = entry.element.getBoundingClientRect().top;
            }
          }
        }
        scheduleScrollLoop();
      }
    }

    // ── Auto-Mark Read ──────────────────────────────────────────────

    let currentVisibleIdx = 0;

    function setCurrentVisibleIdx(idx: number) {
      if (idx == currentVisibleIdx && entries[idx]?.element?.querySelector('.postCard.currentPost')) return;
      const prevEl = entries[currentVisibleIdx]?.element?.querySelector('.postCard');
      if (prevEl) prevEl.classList.remove('currentPost');
      currentVisibleIdx = idx;
      const nextEl = entries[idx]?.element?.querySelector('.postCard');
      if (nextEl) nextEl.classList.add('currentPost');
    }

    function updateSeenPosts() {
      if (isSavedList) return;
      if (!userHasScrolled) return;

      const cr = container.getBoundingClientRect();
      // List rows are read once they've scrolled completely out of view (their title was
      // passed); full posts already when their bottom reaches the upper third.
      const threshold = viewModeRef.current == 'list' ? cr.top : cr.top + cr.height * (1 / 3);
      const [first, last] = renderedRangeRef.current;
      let changed = false;

      // Find the last rendered post whose bottom is above the threshold.
      // All posts before the rendered range have scrolled above the viewport and are also read.
      let lastToMarkIdx = first - 1;
      for (let i = first; i <= last; i++) {
        const entry = entries[i];
        if (!entry.element || !entry.loaded) continue;
        if (entry.element.getBoundingClientRect().bottom <= threshold)
          lastToMarkIdx = i;
      }

      for (let i = 0; i <= lastToMarkIdx; i++) {
        const entry = entries[i];
        if (entry.isRead || entry.manuallyUnread) continue;
        entry.isRead = true;
        onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }

      if (changed) { setKeepUndo(null); updateMarkAllRead(); }
    }

    function isLastPostBottomVisible() {
      if (!entries.length) return false;
      const lastEntry = entries[entries.length - 1];
      if (!lastEntry.element || !lastEntry.loaded) return false;
      const cr = container.getBoundingClientRect();
      const lastRect = lastEntry.element.getBoundingClientRect();
      return lastRect.bottom <= cr.bottom + 1;
    }

    function markAllVisibleUnreadRead() {
      let changed = false;

      for (const entry of entries) {
        if (entry.isRead || entry.manuallyUnread) continue;
        entry.isRead = true;
        onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }

      if (changed) { setKeepUndo(null); updateMarkAllRead(); }
      return changed;
    }

    function markAllReadAtLastPostBottom() {
      if (!userHasScrolled || !isLastPostBottomVisible()) return false;
      markAllReadVisibleRef.current = true;
      markAllReadEverVisibleRef.current = true;
      return markAllVisibleUnreadRead();
    }

    let markReadTimer: ReturnType<typeof setTimeout> | null = null;

    function scheduleMarkRead() {
      if (isSavedList) return;
      if (viewModeRef.current == 'list') return;
      if (markAllReadAtLastPostBottom()) return;

      if (markReadTimer) clearTimeout(markReadTimer);
      markReadTimer = setTimeout(() => {
        if (markAllReadAtLastPostBottom()) return;
        if (!markAllReadVisibleRef.current) return;

        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (entry.isRead || entry.manuallyUnread) continue;

          // Rule 2: mark-all-read/end-of-unread visible → mark all (including last when auto-read enabled)
          if (i != entries.length - 1 || markAllReadEverVisibleRef.current) {
            entry.isRead = true;
            onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
            if (entry.element) updatePostStatus(entry.element, entry, true);
          }
        }

        updateMarkAllRead();
      }, MARK_READ_DELAY);
    }

    // ── Event Listeners ─────────────────────────────────────────────

    // ── Track Visible Post ─────────────────────────────────────────

    function trackVisiblePost() {
      if (scrollMode != 'idle') return;
      const cr = container.getBoundingClientRect();
      const [first, last] = renderedRangeRef.current;
      if (first < 0) return;

      const activationY = cr.top + 10;

      // Find the last post whose header has scrolled to or past the activation line.
      let bestIdx = first;
      for (let i = first; i <= last; i++) {
        const el = entries[i].element;
        if (!el) continue;
        const header = el.querySelector('.postHeader');
        if (!header) continue;
        const rect = header.getBoundingClientRect();
        if (rect.top <= activationY) bestIdx = i;
        else break;
      }

      // If only the separator is visible (body fully above viewport), use next post
      if (bestIdx < last) {
        const body = entries[bestIdx].element?.querySelector('.postBody');
        if (body && body.getBoundingClientRect().bottom < cr.top)
          bestIdx++;
      }

      setCurrentVisibleIdx(bestIdx);
      if (onVisiblePostChangeRef.current) {
        const e = entries[bestIdx];
        onVisiblePostChangeRef.current(e.feedId, e.postIndex);
      }
    }

    let rafId = 0;
    const onScroll = () => {
      const nextScrollTop = container.scrollTop;
      lastScrollDirection = nextScrollTop > lastScrollTop ? 1 : nextScrollTop < lastScrollTop ? -1 : 0;
      lastScrollTop = nextScrollTop;

      if (scrollMode == 'scrolling') return;
      if (scrollMode == 'watching') {
        if (Math.abs(container.scrollTop - watchExpectedScrollTop) > 1) {
          scrollMode = 'idle';
          cancelAnimationFrame(scrollLoopId);
        } else return;
      }
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        handleScrollUpdate();
        trackVisiblePost();
        updateSeenPosts();
        scheduleMarkRead();
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });

    // Allow external code (e.g. Space key handler) to cancel the watching phase
    // so that a subsequent scrollBy isn't fought by the scroll state machine.
    const onCancelScroll = () => {
      if (scrollMode != 'idle') {
        cancelAnimationFrame(scrollLoopId);
        goIdle();
      }
    };
    container.addEventListener('cancelscroll', onCancelScroll);

    // ── List Mode Expand/Collapse ──────────────────────────────────
    function collapseListEntry(entryIdx: number) {
      const entry = entries[entryIdx];
      const expandedEl = entry.element?.querySelector('.listExpandedContent');
      if (expandedEl) expandedEl.remove();
      entry.element?.classList.remove('expanded');
      // Sync the cached height to the now-collapsed row. Otherwise the
      // ResizeObserver fires later with a stale (expanded) oldH and applies a
      // large spurious scrollTop compensation that fights snapRowToTop —
      // leaving the freshly-expanded row at the bottom of the viewport — and
      // the virtualization trimmer over-inflates the spacer with the stale
      // height, producing the blank gaps.
      if (entry.element) entry.height = getStableHeight(entry.element, entry.height);
    }

    // Index of a row whose expansion is waiting for its post data (see toggleListExpand).
    // listNavigate steps from it so rapid taps keep advancing instead of re-targeting
    // the still-loading row and toggling it shut once it arrives.
    let pendingExpand: number | null = null;

    // Make sure entry idx is in the DOM, inserting every virtualized row between the
    // rendered range and idx so the range stays contiguous (the trim/extend loops
    // assume every index inside it has an element).
    function ensureRendered(idx: number) {
      if (entries[idx].element) return;
      const [rf, rl] = renderedRangeRef.current;
      if (idx > rl) {
        for (let j = rl + 1; j <= idx; j++) {
          bottomSpacerHeight = Math.max(0, bottomSpacerHeight - entries[j].height);
          insertPost(j);
        }
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;
        renderedRangeRef.current = [rf, idx];
      } else if (idx < rf) {
        let sh = parseFloat(spacer.style.height) || 0;
        for (let j = rf - 1; j >= idx; j--) {
          sh = Math.max(0, sh - entries[j].height);
          insertPost(j, entries[j + 1]?.element ?? bottomSpacer);
        }
        spacer.style.height = `${sh}px`;
        renderedRangeRef.current = [idx, rl];
      }
    }

    function toggleListExpand(idx: number, onDone?: () => void) {
      const entry = entries[idx];
      // Rows beyond the initial fill are virtualized skeletons whose data loads
      // asynchronously. Expanding one before its data arrives silently did nothing,
      // left expandedIndexRef on the collapsed previous row, and on the next step the
      // now-loaded row expanded off-screen and got trimmed into the bottom spacer with
      // its expanded height — a growing blank area and a "stuck" down button.
      if (!entry.loaded) {
        pendingExpand = idx;
        ensureRendered(idx);
        startFetch(idx).then(() => {
          if (pendingExpand != idx) return;
          pendingExpand = null;
          toggleListExpand(idx, onDone);
        });
        return;
      }
      pendingExpand = null;
      const prev = expandedIndexRef.current;
      // Entry was virtualized after scrolling within a large expanded post. Re-insert it.
      ensureRendered(idx);
      if (!entry.element) return;

      // Remember the clicked row's position so we can keep it stable
      const rowRect = entry.element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const rowOffsetFromTop = rowRect.top - containerRect.top;

      // Collapse previous
      if (prev != null && prev != idx)
        collapseListEntry(prev);

      // Toggle current
      if (prev == idx) {
        collapseListEntry(idx);
        expandedIndexRef.current = null;
      } else if (entry.data) {
        const fi = getFeedInfo(entry.feedId);
        const fullEl = createPostElement(
          entry,
          fi ? (fi.shortTitle || fi.title) : '',
          fi?.siteUrl,
          'full',
          true,
          undefined,
          isSavedList,
          isSavedList ? (hideFollowRef.current || (appStateRef.current?.currentUser?.feeds.includes(entry.feedId) ?? false)) : undefined,
        );
        fullEl.classList.add('listExpandedContent');
        fullEl.classList.remove('postItem');
        entry.element.appendChild(fullEl);
        entry.element.classList.add('expanded');
        expandedIndexRef.current = idx;
        // Notify parent that the visible post changed so currentPostIndex updates
        // (otherwise the down nav-button never sees the user reach the last item
        // and can't enter "More" long-press mode without a manual scroll).
        setCurrentVisibleIdx(idx);
        onVisiblePostChangeRef.current?.(entry.feedId, entry.postIndex);
        if (entry.data.externalScripts)
          loadExternalScripts(entry.data.externalScripts, undefined, isEmbedScriptAllowed);

        if (!isSavedList && !entry.isRead && !entry.manuallyUnread) {
          entry.isRead = true;
          onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
          const dot = entry.element.querySelector(':scope > .readDot');
          if (dot) { dot.classList.remove('unread'); dot.classList.add('read'); }
          entry.element.classList.add('read');
          updateMarkAllRead();
        }
      }

      // Restore scroll so the clicked row stays in the same screen position
      const newRowRect = entry.element.getBoundingClientRect();
      const newOffset = newRowRect.top - containerRect.top;
      const drift = newOffset - rowOffsetFromTop;
      if (Math.abs(drift) > 1)
        container.scrollTop += drift;

      // Keep the cached height in sync with the (now expanded or collapsed)
      // row so later ResizeObserver/virtualization math uses the real height,
      // not the stale list-row estimate.
      entry.height = getStableHeight(entry.element, entry.height);

      // Scrolling inside a tall expanded post trims the rows below it out of the
      // DOM (their height moves into the bottom spacer). Collapsing then leaves
      // the blank spacer filling the viewport until the next scroll event — which
      // never comes if scrollTop didn't change. Re-run the virtualizer now so the
      // rows below are re-inserted immediately.
      handleScrollUpdate();
      onDone?.();
    }

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // List mode: clicking a row toggles expand/collapse
      if (viewModeRef.current == 'list') {
        const actionBtn = target.closest('[data-action]') as HTMLElement | null;
        if (!actionBtn) {
          const listRow = target.closest('.postItem.listMode') as HTMLElement | null;
          if (listRow) {
            const feedId = listRow.dataset.feedId!;
            const postNumber = Number(listRow.dataset.postNumber);
            const idx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postNumber);
            if (idx >= 0) toggleListExpand(idx);
            return;
          }
        }
        // Fall through to action button handling for expanded content
      }

      const actionBtn = target.closest('[data-action]') as HTMLElement | null;
      if (!actionBtn) return;

      if (actionBtn.dataset.action == 'removeSaved') {
        const errorEl = actionBtn.closest('.postLoadError') as HTMLElement | null;
        if (errorEl) removeFailedSavedPost(errorEl);
        return;
      }

      const postItem = actionBtn.closest('.postItem') as HTMLElement | null;
      const article = postItem?.querySelector('article[data-feed-id]') as HTMLElement | null;
      if (!article) return;

      const feedId = article.dataset.feedId!;
      const postNumber = Number(article.dataset.postNumber);
      const action = actionBtn.dataset.action;

      const entryIdx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postNumber);
      if (entryIdx < 0) return;
      const entry = entries[entryIdx];

      if (action == 'toggleRead') {
        toggleKeepUnreadFn.current(feedId, postNumber);
      } else if (action == 'toggleStar') {
        entry.isStarred = !entry.isStarred;
        onMarkRef.current(feedId, postNumber, 'star', entry.isStarred);
        if (entry.element) updatePostStatus(entry.element, entry, true);
      } else if (action == 'toggleReadLater') {
        entry.isReadLater = !entry.isReadLater;
        onMarkRef.current(feedId, postNumber, 'readLater', entry.isReadLater);
        if (entry.element) updatePostStatus(entry.element, entry, true);
      } else if (action == 'share') {
        onShareRef.current?.(feedId, postNumber, entry.data?.title || '', entry.data?.link || '', e.clientX, e.clientY);
      } else if (action == 'viewOriginal') {
        onClickRef.current(feedId, postNumber);
      } else if (action == 'follow') {
        const fe = feedEngineRef.current;
        if (!fe) return;
        const actionSlot = postItem!.querySelector('.followActionSlot') as HTMLElement | null;
        const sepSlot = postItem!.querySelector('.followSepSlot') as HTMLElement | null;

        // Replace both slots with spinners
        const mkSpinner = (size: number) => {
          const s = createSpinnerSvg();
          s.setAttribute('width', String(size));
          s.setAttribute('height', String(size));
          return s;
        };
        const spinnerA = newHtml('div', {
          class: 'followActionSlot',
          style: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: '0' },
        }, mkSpinner(20));
        const spinnerSep = newHtml('button', {
          class: 'separatorButton followSepSlot',
          style: { pointerEvents: 'none' },
        }, newHtml('span', { class: 'separatorLabel' }, mkSpinner(14) as any));
        actionSlot?.replaceWith(spinnerA);
        sepSlot?.replaceWith(spinnerSep);

        (async () => {
          let success = false;
          try {
            await fe.subscribe(feedId);
            success = true;
          } catch (err) {
            console.error('Follow failed:', err);
          }
          const isNowSubscribed = success || (appStateRef.current?.currentUser?.feeds.includes(feedId) ?? false);
          spinnerA.replaceWith(createFollowActionSlot(isNowSubscribed));
          if (isNowSubscribed) {
            spinnerSep.remove();
          } else {
            // Restore the '+ Follow' button on failure
            spinnerSep.replaceWith(createFollowSepSlot(false)!);
          }
        })();
      }
    };
    container.addEventListener('click', onClick);

    // Enter on focused list row expands it. Space is intentionally excluded so it
    // scrolls the post content (handled in PostViewFocus) instead of collapsing.
    const onKeyDown = (e: KeyboardEvent) => {
      if (viewModeRef.current != 'list') return;
      if (e.key != 'Enter') return;
      const target = e.target as HTMLElement;
      const listRow = target.closest('.postItem.listMode') as HTMLElement | null;
      if (!listRow || target.closest('[data-action]') || target.closest('a')) return;
      e.preventDefault();
      const feedId = listRow.dataset.feedId!;
      const postNumber = Number(listRow.dataset.postNumber);
      const idx = entries.findIndex(en => en.feedId == feedId && en.postIndex == postNumber);
      if (idx >= 0) toggleListExpand(idx);
    };
    container.addEventListener('keydown', onKeyDown);

    // ── Initial Render ──────────────────────────────────────────────

    const clampedStart = Math.max(0, Math.min(startAt, entries.length - 1));
    const firstIdx = Math.max(0, clampedStart - 1);
    const lastIdx = Math.min(entries.length - 1, clampedStart + 1);
    renderedRangeRef.current = [firstIdx, lastIdx];

    for (let i = firstIdx; i <= lastIdx; i++)
      insertPost(i);

    // Fill viewport below
    {
      let last = lastIdx;
      const cr = container.getBoundingClientRect();
      while (last < entries.length - 1) {
        const el = entries[last].element;
        if (el && el.getBoundingClientRect().bottom > cr.bottom + cr.height) break;
        last++;
        insertPost(last);
      }
      renderedRangeRef.current[1] = last;

      // Initialize bottom spacer for all entries below rendered range
      bottomSpacerHeight = 0;
      for (let i = last + 1; i < entries.length; i++)
        bottomSpacerHeight += entries[i].height;
      bottomSpacer.style.height = `${bottomSpacerHeight}px`;
    }

    // Set initial scroll position to show startAt post (after content below is
    // laid out, otherwise scrollTop gets clamped by an insufficient scrollHeight).
    // For an append-only growth the prefix layout is unchanged, so restore the prior
    // scroll position to avoid a jump (e.g. clicking "Show more" in a browse-only feed).
    container.scrollTop = isAppend ? preservedScrollTop : (parseFloat(spacer.style.height) || 0);

    // The fill loop above measured everything at scrollTop 0, i.e. against the top of the
    // scroll area — but we just jumped to the top spacer's end. Whenever that spacer is
    // taller than a viewport (startAt deep in the list: a hash restore after returning
    // from an external site, or a background poll that prepended posts), the filled band
    // sits entirely above the final viewport and the user faces blank space that only
    // heals on the first scroll. Re-run the virtualization against the position we
    // actually landed on. Most visible in list mode, where a handful of 32px rows leaves
    // most of the screen white; in full mode the few rendered posts usually cover it.
    handleScrollUpdate();
    lastScrollTop = container.scrollTop;

    updateSeenPosts();
    setCurrentVisibleIdx(clampedStart);

    // If all posts fit on screen (can't scroll), mark them as seen
    // but don't set markAllReadEverVisible yet — wait for user interaction.
    const fitCheckTimer = setTimeout(() => {
      if (container.scrollHeight <= container.clientHeight + 1)
        updateSeenPosts();
    }, 0);

    // ── Imperative Handle Functions ─────────────────────────────────

    scrollToPostFn.current = (feedId, postIndex) => {
      const idx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postIndex);
      if (idx < 0) return;

      const [first, last] = renderedRangeRef.current;

      // If outside rendered range, teleport close to it
      if (idx < first || idx > last) {
        let sh = 0;
        for (let i = 0; i < Math.max(0, idx - 1); i++)
          sh += entries[i].height;

        spacer.style.height = `${sh}px`;

        for (let i = first; i <= last; i++) {
          if (entries[i].element) {
            viewportObserver.unobserve(entries[i].element!);
            resizeObserver.unobserve(entries[i].element!);
            resizeMap.delete(entries[i].element!);
            entries[i].element!.remove();
            entries[i].element = null;
          }
        }

        const newFirst = Math.max(0, idx - 1);
        const newLast = Math.min(entries.length - 1, idx + BUFFER_POSTS);
        renderedRangeRef.current = [newFirst, newLast];

        for (let i = newFirst; i <= newLast; i++)
          insertPost(i);

        bottomSpacerHeight = 0;
        for (let i = newLast + 1; i < entries.length; i++)
          bottomSpacerHeight += entries[i].height;
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;

        container.scrollTop = sh;
      }

      // Mark previous visible post as read when navigating forward
      if (!isSavedList && idx > currentVisibleIdx) {
        const prev = entries[currentVisibleIdx];
        if (prev && !prev.isRead && !prev.manuallyUnread) {
          prev.isRead = true;
          onMarkRef.current(prev.feedId, prev.postIndex, 'read', true);
          if (prev.element) updatePostStatus(prev.element, prev, true);
        }
      }

      setCurrentVisibleIdx(idx);
      updateSeenPosts();

      if (!entries[idx].element) return;
      beginScrolling({ kind: 'post', idx });
    };

    scrollPageFn.current = (direction) => {
      const pageHeight = container.clientHeight * 0.9;
      // Clamped to the real scroll range, so the animation ends exactly at the bottom
      // instead of writing past it (which iOS Safari doesn't always clamp).
      const maxTop = container.scrollHeight - container.clientHeight;
      beginScrolling({ kind: 'absolute', position: Math.max(0, Math.min(maxTop, container.scrollTop + direction * pageHeight)) });
    };

    scrollToBottomFn.current = () => {
      // Scroll fully to the end of the viewport so the bottom action buttons
      // (Mark all read / Next Feed) are reachable and the down-nav long-press
      // works. Beats button.scrollIntoView({block:'center'}), which only centers
      // the button and can leave the container short of its true bottom.
      markUserInteracted();
      beginScrolling({ kind: 'bottom' });
    };

    // Offset of the first post from its snapped top position (negative while the top content,
    // e.g. the preview's FeedTile, still pushes it down).
    const firstPostScrolledPast = () => {
      const el = entries[0]?.element;
      if (!el) return undefined;
      const offset = viewModeRef.current == 'list' ? 4 : 11;
      return container.getBoundingClientRect().top - el.getBoundingClientRect().top - offset;
    };

    prevPostOrScrollToTopFn.current = (thresholdPx) => {
      // The top content counts as the item before the first post. Checked before the settled-state
      // guard below: after navigating to the first post the renderer keeps 'watching' it for a few
      // seconds, but the first post's position is known then.
      // Already showing the top content: nothing above it (claim the key so the caller doesn't fall
      // back to "previous post", which would be the first post again).
      if (hasTopContentRef.current && currentVisibleIdx == 0 && container.scrollTop <= 1) return true;
      if (hasTopContentRef.current && currentVisibleIdx == 0 && scrollMode != 'scrolling' && container.scrollTop > 1) {
        const past = firstPostScrolledPast();
        if (past != undefined && past <= thresholdPx) {
          beginScrolling({ kind: 'absolute', position: 0 });
          return true;
        }
      }
      // While a programmatic scroll is still animating, trackVisiblePost is
      // paused (scrollMode != 'idle'), so currentVisibleIdx is stale and its
      // geometry is unreliable. A rapid repeated 'k' here would otherwise keep
      // re-triggering scroll-to-top on the stale post ("stutter") instead of
      // stepping up. Only apply the snap-to-top behavior once settled.
      if (scrollMode != 'idle') return false;
      const entry = entries[currentVisibleIdx];
      if (!entry?.element) return false;
      const r = entry.element.getBoundingClientRect();
      const cr = container.getBoundingClientRect();
      const offset = viewModeRef.current == 'list' ? 4 : 11;
      // How far the viewport is scrolled past this post's snapped top position
      // (matches getScrollTargetPos so we land exactly where the post would snap).
      const scrolledPast = cr.top - r.top - offset;
      if (scrolledPast <= thresholdPx) return false;
      beginScrolling({ kind: 'post', idx: currentVisibleIdx });
      return true;
    };

    nextFromTopContentFn.current = (thresholdPx) => {
      if (!hasTopContentRef.current || scrollMode == 'scrolling' || currentVisibleIdx != 0) return false;
      const past = firstPostScrolledPast();
      if (past == undefined || past >= -thresholdPx) return false;
      beginScrolling({ kind: 'post', idx: 0 });
      return true;
    };

    getCurrentVisibleFn.current = () => {
      const entry = entries[currentVisibleIdx];
      return entry ? [entry.feedId, entry.postIndex] : null;
    };

    moveToTopFn.current = (immediately) => {
      if (immediately) {
        goIdle();
        const [first, last] = renderedRangeRef.current;
        for (let i = first; i <= last; i++) {
          if (entries[i].element) {
            viewportObserver.unobserve(entries[i].element!);
            resizeObserver.unobserve(entries[i].element!);
            resizeMap.delete(entries[i].element!);
            entries[i].element!.remove();
            entries[i].element = null;
          }
        }

        spacer.style.height = '0px';
        const newLast = Math.min(entries.length - 1, BUFFER_POSTS);
        renderedRangeRef.current = [0, newLast];

        for (let i = 0; i <= newLast; i++)
          insertPost(i);

        bottomSpacerHeight = 0;
        for (let i = newLast + 1; i < entries.length; i++)
          bottomSpacerHeight += entries[i].height;
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;

        container.scrollTop = 0;
      } else {
        beginScrolling({ kind: 'absolute', position: 0 });
      }
    };

    // ── Batch Mark Operations ────────────────────────────────────────

    markPostReadUnlessLockedFn.current = (feedId, postIndex) => {
      if (isSavedList) return;
      const entry = entries.find(e => e.feedId == feedId && e.postIndex == postIndex);
      if (!entry || entry.isRead || entry.keepUnread || entry.manuallyUnread) return;
      entry.isRead = true;
      onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
      if (entry.element) updatePostStatus(entry.element, entry, true);
      setKeepUndo(null);
      updateMarkAllRead();
    };

    markAllReadFn.current = () => {
      if (isSavedList) return;
      let changed = false;
      for (const entry of entries) {
        if (entry.isRead) continue;
        entry.isRead = true;
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    markAllReadUnlessLockedFn.current = () => {
      if (isSavedList) return;
      let changed = false;
      for (const entry of entries) {
        if (entry.isRead || entry.keepUnread || entry.manuallyUnread) continue;
        entry.isRead = true;
        onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    markAllUnreadFn.current = () => {
      if (isSavedList) return;
      let changed = false;
      for (const entry of entries) {
        if (!entry.isRead) continue;
        entry.isRead = false;
        entry.manuallyUnread = true;
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    keepAllUnreadFn.current = () => {
      if (isSavedList) return;
      // Snapshot every post's read/lock state so the next "Items Locked" click can undo.
      const snapshot: KeepSnapshot[] = entries.map(e =>
        ({ feedId: e.feedId, postIndex: e.postIndex, isRead: e.isRead, keepUnread: e.keepUnread, manuallyUnread: e.manuallyUnread }));
      let changed = false;
      for (const entry of entries) {
        if (entry.keepUnread) continue;
        entry.keepUnread = true;
        entry.manuallyUnread = true;
        entry.isRead = false;
        onMarkRef.current(entry.feedId, entry.postIndex, 'read', false);
        if (entry.element) updatePostStatus(entry.element, entry, true);
        changed = true;
      }
      if (changed) { setKeepUndo(snapshot); updateMarkAllRead(); }
    };

    toggleKeepUnreadFn.current = (feedId, postIndex) => {
      if (isSavedList) return;
      // A manual per-post lock/unlock invalidates the "Keep All" undo.
      setKeepUndo(null);
      const entry = entries.find(e => e.feedId == feedId && e.postIndex == postIndex);
      if (!entry) return;
      entry.keepUnread = !entry.keepUnread;
      entry.manuallyUnread = entry.keepUnread;
      entry.isRead = !entry.keepUnread;
      onMarkRef.current(feedId, postIndex, 'read', entry.isRead);
      if (entry.element) updatePostStatus(entry.element, entry, true);
      updateMarkAllRead();
    };

    markOlderThanReadFn.current = (days) => {
      if (isSavedList) return;
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      let changed = false;
      for (const entry of entries) {
        if (entry.isRead || !entry.loaded || !entry.data?.pubDate) continue;
        if (new Date(entry.data.pubDate).getTime() < cutoff) {
          entry.isRead = true;
          onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
          if (entry.element) updatePostStatus(entry.element, entry, true);
          changed = true;
        }
      }
      if (changed) updateMarkAllRead();
    };

    updatePostStatusFn.current = (feedId, postIndex, type, value) => {
      const idx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postIndex);
      if (idx < 0) return;
      const entry = entries[idx];

      if (type == 'read') entry.isRead = value;
      else if (type == 'star') entry.isStarred = value;
      else if (type == 'readLater') entry.isReadLater = value;

      if (type == 'read') updateMarkAllRead();

      if (!entry.loaded || !entry.element?.isConnected) return;
      updatePostStatus(entry.element, entry, true);
    };

    // ── List Mode Navigate (j/k) ──────────────────────────────────
    // Snap a freshly expanded list row to the top of the viewport. Avoid
     // beginScrolling here: it animates from the user's prior scroll position
     // (often deep inside the previous, now-collapsed post), which scrolls
     // through stale content and traverses a lot of distance in long lists.
    const snapRowToTop = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      const cr = container.getBoundingClientRect();
      container.scrollTop = container.scrollTop + r.top - cr.top + (viewModeRef.current == 'list' ? 4 : 11);
    };

    // Returns true if it expanded a different row, false at the boundary (so the caller can
     // fall through to its own end-of-list handling — e.g. focusing the Next Feed button).
    // Snap + focus once the row is actually expanded (possibly after its data loads).
    const expandRowAndSnap = (idx: number) => toggleListExpand(idx, () => {
      const el = entries[idx].element;
      if (!el) return;
      snapRowToTop(el);
      el.focus({ preventScroll: true });
    });

    listNavigateFn.current = (direction: 1 | -1) => {
      const current = pendingExpand ?? expandedIndexRef.current;

      if (current != null) {
        const next = current + direction;
        if (next < 0 || next >= entries.length) return false;
        expandRowAndSnap(next);
        return true;
      }

      // Nothing expanded: find first visible row and expand it
      const containerRect = container.getBoundingClientRect();
      let targetIdx = direction == 1 ? 0 : entries.length - 1;
      for (let i = 0; i < entries.length; i++) {
        const el = entries[i].element;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top >= containerRect.top && rect.bottom <= containerRect.bottom) {
          targetIdx = i;
          break;
        }
      }
      if (targetIdx < 0 || targetIdx >= entries.length) return false;
      expandRowAndSnap(targetIdx);
      return true;
    };

    // ── Cleanup ─────────────────────────────────────────────────────

    return () => {
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('cancelscroll', onCancelScroll);
      container.removeEventListener('click', onClick);
      container.removeEventListener('keydown', onKeyDown);
      container.removeEventListener('wheel', onUserWheel);
      container.removeEventListener('touchstart', onUserTouchStart);
      container.removeEventListener('touchmove', onUserTouchMove);
      container.removeEventListener('pointerdown', markUserInteracted);
      container.removeEventListener('keydown', markUserInteracted);
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(scrollLoopId);
      scrollMode = 'idle';
      if (markReadTimer) clearTimeout(markReadTimer);
      clearTimeout(fitCheckTimer);

      // Capture mark-all-read visibility before disconnecting observer
      // Use "ever visible" flag - if user scrolled to bottom at any point, mark posts as read
      const wasMarkAllReadEverVisible = markAllReadEverVisibleRef.current;

      viewportObserver.disconnect();
      resizeObserver.disconnect();
      markAllObserver.disconnect();

      const containerRect = container.getBoundingClientRect();
      const containerIsMeasurable =
        container.isConnected &&
        container.clientHeight > 1 &&
        container.scrollHeight > 1 &&
        containerRect.height > 1 &&
        containerRect.width > 1;
      const fullyVisibleWithoutScrolling =
        userHasScrolled &&
        containerIsMeasurable &&
        container.scrollHeight <= container.clientHeight + 1;

      // viewModeRef.current == effectViewMode: skip when this cleanup is due to a view-mode
      // switch (the new viewMode is already in the ref by the time cleanup runs).
      if (viewModeRef.current == effectViewMode && !isSavedList && viewModeRef.current != 'list' && (wasMarkAllReadEverVisible || fullyVisibleWithoutScrolling)) {
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          if (!entry.isRead && !entry.manuallyUnread) {
            entry.isRead = true;
            onMarkRef.current(entry.feedId, entry.postIndex, 'read', true);
          }
        }
      }
    };
  }, [posts, props.viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>
    <div className="postsContainer" ref={containerRef} />
    {hasTopContent && createPortal(props.topContent, topHost)}
  </>;
});

export default PostRenderer;
